import { isAxiosError } from 'axios';

export type WorkflowCommunityPublishSource =
  'workflow_share_modal' | 'workflow_banner' | 'tool_share_modal' | 'tool_banner';
export type WorkflowCommunityPublishLinkActionSource = WorkflowCommunityPublishSource | 'publish_modal_terminal';
export type WorkflowCommunityPublishMode = 'initial' | 'update';

/**
 * Which resource is being published. `source` says where the user clicked, which is a different
 * question — a breakdown on `source` splits tool traffic across two values and mixes in the workflow
 * ones. Required, not optional: the modal and the panel each build their own context, and an optional
 * field would let a fifth one ship unset the way `toolId` already did.
 */
export type WorkflowCommunityPublishSurface = 'workflow' | 'tool';

/** Where the listing goes: Figma Community, or one Figma org. */
export type WorkflowCommunityPublishDestination = 'community' | 'org';

export interface WorkflowCommunityPublishTrackingContext {
  source: WorkflowCommunityPublishSource;
  surface: WorkflowCommunityPublishSurface;
  mode: WorkflowCommunityPublishMode;
  destination: WorkflowCommunityPublishDestination;
  /** The org's plan id, when `destination` is `org` and the org is known. Never its name. */
  orgPlanId?: string;
  workflowId?: string;
  /** Set when the event is about a tool: its `weave_app_id` (= `WorkflowTool.id`). */
  toolId?: string;
  resourceId?: string | null;
}

export interface WorkflowCommunityPublishLinkTrackingContext extends Omit<
  WorkflowCommunityPublishTrackingContext,
  'source'
> {
  source: WorkflowCommunityPublishLinkActionSource;
}

const getErrorProp = (error: unknown, key: string): unknown => {
  if (!error || typeof error !== 'object' || !(key in error)) return undefined;
  return (error as Record<string, unknown>)[key];
};

const getNumberProp = (error: unknown, key: string): number | undefined => {
  const value = getErrorProp(error, key);
  return typeof value === 'number' ? value : undefined;
};

const getStringProp = (error: unknown, key: string): string | undefined => {
  const value = getErrorProp(error, key);
  return typeof value === 'string' ? value : undefined;
};

export const getCommunityPublishErrorPayload = (error: unknown) => ({
  errorName: error instanceof Error ? error.name : typeof error,
  status: isAxiosError(error) ? error.response?.status : getNumberProp(error, 'status'),
  s3ErrorCode: getStringProp(error, 's3ErrorCode'),
  imageBytes: getNumberProp(error, 'imageBytes'),
});
